import { useEffect, useRef, useState } from 'react';
import { FiSend } from 'react-icons/fi';

import { Input } from '@/shared/components/ui';
import type { ActionIntent, MessageComponent } from '../../services/agentDraftApi';
import { DatabasePickerMessage } from './DatabasePickerMessage';
import { FunnelConfirmMessage } from './FunnelConfirmMessage';
import { FunnelPickerMessage } from './FunnelPickerMessage';
import { MultiChoiceMessage } from './MultiChoiceMessage';
import { UserPickerMessage } from './UserPickerMessage';

interface ChatPanelProps {
  messages: Array<{ role: 'assistant' | 'user'; content: string }>;
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  pendingComponent?: MessageComponent;
  onActionConfirm?: (params: { displayMessage: string; intent: ActionIntent }) => void;
}

export function ChatPanel({ messages, onSendMessage, isLoading, pendingComponent, onActionConfirm }: ChatPanelProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    onSendMessage(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div data-testid="chat-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRight: '1px solid #e2e8f0' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>Assistente de Criação</div>
        <div style={{ fontSize: '12px', color: '#64748b' }}>Me conte sobre o agente que você quer criar</div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {messages.map((msg, i) => (
          <div
            key={i}
            data-testid={`chat-message-${msg.role}-${i}`}
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            {msg.role === 'assistant' && (
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#8b5cf6',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '11px',
                }}
              >
                ✦
              </div>
            )}
            <div
              style={{
                background: msg.role === 'user' ? '#8b5cf6' : '#fff',
                color: msg.role === 'user' ? '#fff' : '#334155',
                border: msg.role === 'assistant' ? '1px solid #e2e8f0' : 'none',
                borderRadius: '12px',
                padding: '10px 14px',
                maxWidth: '80%',
                fontSize: '13px',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: '#8b5cf6',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '11px',
              }}
            >
              ✦
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '10px 14px',
                fontSize: '13px',
                color: '#94a3b8',
              }}
            >
              Pensando...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Componente interativo */}
      {pendingComponent && (
        <div style={{ padding: '0 16px 12px' }}>
          {pendingComponent.type === 'user_picker' && (
            <UserPickerMessage
              users={pendingComponent.data.users}
              onConfirm={onActionConfirm ?? ((p) => onSendMessage(p.displayMessage))}
            />
          )}
          {pendingComponent.type === 'funnel_confirm' && (
            <FunnelConfirmMessage
              name={pendingComponent.data.name}
              stages={pendingComponent.data.stages}
              onConfirm={onActionConfirm ?? ((p) => onSendMessage(p.displayMessage))}
              onSkip={(p) => onSendMessage(p.displayMessage)}
            />
          )}
          {pendingComponent.type === 'database_picker' && (
            <DatabasePickerMessage
              databases={pendingComponent.data.databases}
              multi={pendingComponent.data.multi ?? false}
              onSelect={onActionConfirm ?? ((p) => onSendMessage(p.displayMessage))}
              onSkip={(p) => onSendMessage(p.displayMessage)}
            />
          )}
          {pendingComponent.type === 'funnel_picker' && (
            <FunnelPickerMessage
              funnels={pendingComponent.data.funnels}
              allowCreate={pendingComponent.data.allowCreate}
              onPick={onActionConfirm ?? ((p) => onSendMessage(p.displayMessage))}
              onCreateNew={(p) => onSendMessage(p.displayMessage)}
            />
          )}
          {pendingComponent.type === 'multi_choice' && (
            <MultiChoiceMessage
              question={pendingComponent.data.question}
              mode={pendingComponent.data.mode}
              options={pendingComponent.data.options}
              {...(pendingComponent.data.submitLabel !== undefined
                ? { submitLabel: pendingComponent.data.submitLabel }
                : {})}
              onConfirm={onActionConfirm ?? ((p) => onSendMessage(p.displayMessage))}
            />
          )}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Descreva o que deseja..."
            disabled={isLoading}
            data-testid="chat-input"
            style={{ flex: 1, fontSize: '13px' }}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            data-testid="chat-send-button"
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: 'none',
              background: isLoading || !input.trim() ? '#e2e8f0' : '#8b5cf6',
              color: isLoading || !input.trim() ? '#94a3b8' : '#fff',
              fontSize: '13px',
              cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <FiSend size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
